import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ActionBar,
  ActionBarVariant,
  ChakraBox,
  ChakraFlex,
  ChakraVStack,
  FAIconName,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import Footer from './Footer';
import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import PatientInfoCard from './PatientInfoCard';
import PatientAppointments from './PatientAppointments';
import AppointmentsPanel from './AppointmentsPanel';
import {
  buildWeekBuckets,
  appointmentsPanelReducer,
  initialAppointmentsPanelState,
} from './AppointmentsPanel/utils';
import AttendeeAndAppointmentTypeSelection from './AttendeeAndAppointmentTypeSelection';
import RecurringTypeAndAttendees from './RecurringTypeAndAttendees';
import TimeSlotSelection from './TimeSlotSelection';
import ProviderOnlySelection from './ProviderOnlySelection';
import ConfirmationStep from './ConfirmationStep';
import { ConfirmationAppointment } from './ConfirmationStep/AppointmentList';
import ConfirmationStepLegacy from './ConfirmationStepLegacy';
import FollowUpScheduling from './FollowUpScheduling';
import useFollowUpAvailability from './hooks/useFollowUpAvailability';
import useFollowUpScheduling from './hooks/useFollowUpScheduling';
import { DateTime } from 'luxon';
import { useGetAssessmentsListQuery } from '~/store/services/assessments';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import {
  extractIANATimezone,
  extractPatientAvailabilityPreferences,
  getCriticalDataMissingActionBarConfig,
  getSubmitErrorActionBarConfig,
  getIqErrorActionBarConfig,
  getNoRecommendationsActionBarConfig,
  getNoAvailabilityActionBarConfig,
  getFollowUpWindowExpiredActionBarConfig,
  getAdmitDate,
  getTieredProviderMap,
  getTreatmentEpisodeId,
  getTreatmentProgramDisplay,
  toUtcIsoString,
  filterProvidersWithAvailability,
  getPreferredProviderRoleName,
  getTotalProviderCount,
  extractProviderIds,
  getProviderPrimaryTag,
} from './utils';
import {
  extractErrorCode,
  getBookingErrorMessage,
  parseMissingFieldErrors,
  MissingFieldError,
} from '~/lib/util/api-error.util';
import { normalizeTimezone } from '~/lib/util/timezone.util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import {
  useGetPatientClinicalProfileQuery,
  useGetPatientTreatmentEpisodesQuery,
  useGetProviderTeamAssignmentsQuery,
} from '~/store/services/clinicalData';
import { useGetInquiryDetailByIdQuery } from '~/store/services/inquiry';
import { useGetPatientQuery } from '~/store/services/patients';
import useProviderRecommendations from '~/lib/hooks/useProviderRecommendations';
import useSupportChat from '~/lib/hooks/useSupportChat';
import { useDatadog } from '~/lib/hooks/useDatadog';
import useProviderAvailability from './hooks/useProviderAvailability';
import {
  ActionBarMessage,
  ProviderAssignmentSelectionTab,
  defaultIntakeAppointmentTypes,
  MAX_PROVIDERS_FOR_AVAILABILITY_FETCH,
  AVAILABILITY_TOOLTIP_MESSAGE,
} from './constants';
import { getTreatmentProgramCode } from '~/lib/util/schedule/schedule.util';
import useGetAttendees from '~/lib/hooks/useGetAttendees';
import {
  useUpdateProviderTeamAssignmentsMutation,
  useUpdateProviderTeamPreliminaryAssignmentsMutation,
} from '~/store/services/treatmentEpisodes';
import {
  useGetAppointmentDetailsQuery,
  useBatchSaveAppointmentsMutation,
  BatchAppointmentEntry,
} from '~/store/services/appointment';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { Recurrence } from '~/lib/util/calendar.util';
import { ProviderAssignmentAnalyticsEvents } from '~/analytics';
import useProviderAssignmentAnalytics from './hooks/useProviderAssignmentAnalytics';
import {
  validateTimeSlotInTimezone,
  logTimezoneMismatch,
} from './timezoneValidation.util';

const TOP_LEVEL_ID = 'provider-assignment';

type BatchSaveCommonProps = Pick<
  BatchAppointmentEntry,
  | 'hostExternalId'
  | 'ignoreAvailabilitySettings'
  | 'inquiryExternalId'
  | 'patientExternalId'
  | 'providerAttendeeExternalIds'
  | 'recurrenceFrequency'
  | 'timeZoneId'
>;

enum Step {
  INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION,
  PROVIDER_AND_INTAKE_SELECTION,
  PROVIDER_SELECTION,
  RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION,
  FOLLOW_UP_SCHEDULING,
  CONFIRMATION,
}

const STEP_DISPLAY_NAMES: Record<Step, string> = {
  [Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION]:
    'Intake Attendees and Appointment Type Selection',
  [Step.PROVIDER_AND_INTAKE_SELECTION]: 'Provider and Intake Selection',
  [Step.PROVIDER_SELECTION]: 'Provider Selection',
  [Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION]:
    'Recurring Attendees and Appointment Type Selection',
  [Step.FOLLOW_UP_SCHEDULING]: 'Follow-Up Scheduling',
  [Step.CONFIRMATION]: 'Confirmation',
};

const getProviderTier = (
  tieredProviderMap: ReturnType<typeof getTieredProviderMap> | null,
  providerId: string,
): ProviderAssignmentSelectionTab | undefined =>
  (
    Object.entries(tieredProviderMap ?? {}) as [
      ProviderAssignmentSelectionTab,
      Record<string, unknown>,
    ][]
  ).find(([, providers]) => providerId in providers)?.[0];

interface ActionBarMessageContext {
  isCriticalDataMissing: boolean;
  isProviderRecommendationsError: boolean;
  iqErrorMessage: string | undefined;
  isSubmitError: boolean;
  submitMissingFieldsCount: number;
  showNoRecommendationsWarning: boolean;
  showNoAvailabilityWarning: boolean;
  isFollowUpWindowExpired: boolean;
}

const getActionBarMessage = (ctx: ActionBarMessageContext): ActionBarMessage | null => {
  if (ctx.isCriticalDataMissing) return ActionBarMessage.CRITICAL_DATA_MISSING;
  if (ctx.isProviderRecommendationsError && ctx.iqErrorMessage) return ActionBarMessage.IQ_ERROR;
  if (ctx.isSubmitError && ctx.submitMissingFieldsCount > 0) return ActionBarMessage.CRITICAL_DATA_MISSING;
  if (ctx.isSubmitError) return ActionBarMessage.SUBMIT_ERROR;
  if (ctx.showNoRecommendationsWarning) return ActionBarMessage.NO_RECOMMENDATIONS;
  if (ctx.showNoAvailabilityWarning) return ActionBarMessage.NO_AVAILABILITY;
  if (ctx.isFollowUpWindowExpired) return ActionBarMessage.FOLLOW_UP_WINDOW_EXPIRED;
  return null;
};

const ProviderAssignmentModal = () => {
  const { logError } = useDatadog();
  const { createToast } = useToastMessage();
  const { providerAssignmentModal, setComponentVisibility } =
    useComponentPortalContext();
  const { scheduleRecurringAppointments } = useFlags<LDFlags>();
  const isRecurringSchedulingEnabled = !!scheduleRecurringAppointments;

  const sessionId = useMemo(() => crypto.randomUUID(), []);
  const isCompletedRef = useRef(false);
  const tieredProviderMapRef = useRef<ReturnType<
    typeof getTieredProviderMap
  > | null>(null);
  const providerMapRef = useRef<Record<string, GetProviderByIdResponse>>({});
  const viewedStepRef = useRef<Step | null>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const selectedFollowUpsRef = useRef<FollowUpOpening[]>([]);
  const intakeTimeSlotRef = useRef<AppointmentSlot>(null);

  const { inquiryId, patientId, providerTypes } =
    (providerAssignmentModal?.data ?? {}) as {
      inquiryId: string;
      patientId: string;
      providerTypes: CareTeamProviderType[];
    };

  const {
    data: patient,
    isError: isPatientError,
    isFetching: isPatientLoading,
  } = useGetPatientQuery(
    { includeLinkedUsers: true, patientId },
    {
      refetchOnMountOrArgChange: true,
      skip: !patientId,
    },
  );

  const patientTimezone = useMemo(() => {
    const rawTimezone = patient?.timezoneValue || patient?.timezone;
    const cleanedTimezone = extractIANATimezone(rawTimezone);
    const normalized = normalizeTimezone(cleanedTimezone);
    return normalized;
  }, [patient?.timezoneValue, patient?.timezone]);

  useEffect(() => {
    const rawTimezone = patient?.timezoneValue || patient?.timezone;
    const cleanedTimezone = extractIANATimezone(rawTimezone);

    if (cleanedTimezone && patientTimezone !== cleanedTimezone) {
      logError(
        new Error(
          `Patient timezone fallback: invalid timezone "${cleanedTimezone}"`,
        ),
        {
          context: 'ProviderAssignmentModal.patientTimezone',
          patientId,
          rawTimezone,
          cleanedTimezone,
          fallbackTimezone: patientTimezone,
        },
      );
    }
  }, [
    patientTimezone,
    patient?.timezoneValue,
    patient?.timezone,
    patientId,
    logError,
  ]);

  const {
    data: patientClinicalProfile,
    isError: isClinicalProfileError,
    isFetching: isClinicalProfileLoading,
  } = useGetPatientClinicalProfileQuery(patientId, {
    skip: !patientId,
  });

  const {
    data: inquiryDetail,
    isError: isInquiryError,
    isFetching: isInquiryLoading,
  } = useGetInquiryDetailByIdQuery(inquiryId, {
    refetchOnMountOrArgChange: true,
    skip: !inquiryId,
  });

  const {
    data: treatmentEpisodesData,
    isError: isTreatmentEpisodesError,
    isFetching: isTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId, { skip: !patientId });

  const treatmentEpisodeId = useMemo(
    () => getTreatmentEpisodeId(treatmentEpisodesData, inquiryId),
    [treatmentEpisodesData, inquiryId],
  );

  const isEnrolled =
    inquiryDetail?.status?.name === InquiryStatusValue.ENROLLED;

  /**
   * True when the patient is enrolled but the treatment episodes query has
   * settled cleanly (no loading, no network error) with no matching episode.
   * Falling back to the preliminary endpoint in this state would silently
   * write assignments to the wrong resource, so we surface this as a
   * blocking critical-data condition instead.
   */
  const isTreatmentEpisodeMissing =
    isEnrolled &&
    !isTreatmentEpisodesLoading &&
    !isTreatmentEpisodesError &&
    !treatmentEpisodeId;

  const [providerType, setProviderType] = useState<CareTeamProviderType>(
    providerTypes?.[0],
  );
  const { trackPAEvent, setPrimaryTag } = useProviderAssignmentAnalytics({
    inquiryId,
    patientId,
    providerType,
  });

  const [appointmentsPanelState, appointmentsPanelDispatch] = useReducer(
    appointmentsPanelReducer,
    initialAppointmentsPanelState,
  );
  const [panelFocusWeekIndex, setPanelFocusWeekIndex] = useState<number | undefined>(undefined);

  const [intakeTimeSlot, setIntakeTimeSlot] = useState<AppointmentSlot>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string>(null);
  const [selectedTab, setSelectedTab] =
    useState<ProviderAssignmentSelectionTab>(
      ProviderAssignmentSelectionTab.STRONG,
    );
  const [intakeAppointmentType, setIntakeAppointmentType] = useState<
    Partial<AppointmentType>
  >(defaultIntakeAppointmentTypes[providerTypes?.[0]]);
  const [intakeAttendees, setIntakeAttendees] = useState<FamilyAttendeeWithSelection[]>([]);
  const [recurringAppointmentType, setRecurringAppointmentType] = useState<
    Partial<AppointmentType> | null
  >(null);
  const [recurringAttendees, setRecurringAttendees] = useState<
    FamilyAttendeeWithSelection[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);
  const [isDismissedCriticalDataError, setIsDismissedCriticalDataError] =
    useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [submitMissingFields, setSubmitMissingFields] = useState<
    MissingFieldError[]
  >([]);
  const [step, setStep] = useState(
    Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION,
  );
  const [previousStep, setPreviousStep] = useState<Step | null>(null);

  const committedAssignmentsRef = useRef<
    Array<Omit<ProviderTeamAssignment, 'assignmentId' | 'startDate'>>
  >([]);

  const { data: providerTeamAssignmentsData } =
    useGetProviderTeamAssignmentsQuery(patientId, { skip: !patientId });

  useEffect(() => {
    if (!inquiryId || !patientId) return;
    trackPAEvent(
      ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_INITIATED_INQUIRY,
      { sessionId },
    );
  }, [inquiryId, patientId, sessionId, trackPAEvent]);

  useEffect(() => {
    if (viewedStepRef.current === step) return;
    viewedStepRef.current = step;
    const stepName = STEP_DISPLAY_NAMES[step];
    trackPAEvent(
      ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_STEP_VIEWED,
      { step: stepName },
    );
  }, [step, trackPAEvent]);

  useEffect(() => {
    if (step === Step.CONFIRMATION && scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    const existingAssignments = isEnrolled
      ? (
        providerTeamAssignmentsData?.treatmentEpisode
          ?.providerTeamAssignments ?? []
      ).map(({ providerId, role, temporary, endDate }) => ({
        providerId,
        role,
        temporary,
        endDate,
      }))
      : (
        providerTeamAssignmentsData?.inquiry?.providerTeamAssignments ?? []
      ).map(({ providerId, role }) => ({
        providerId,
        role,
        temporary: false,
      }));
    committedAssignmentsRef.current = existingAssignments;
  }, [inquiryId, isEnrolled, providerTeamAssignmentsData]);

  const {
    recommendationsData,
    isError: isProviderRecommendationsError,
    isFetching: isProviderRecommendationsLoading,
    iqErrorMessage,
    missingFields,
    providerMap,
  } = useProviderRecommendations({
    patientId,
    providerTypes,
    skip: ![
      Step.PROVIDER_AND_INTAKE_SELECTION,
      Step.PROVIDER_SELECTION,
    ].includes(step),
  });

  /** Select or deselect the intake time slot */
  const onSlotSelect = useCallback(
    (slot: AppointmentSlot) => {
      const currentSlot = intakeTimeSlotRef.current;
      const isDeselecting =
        slot?.startTime === currentSlot?.startTime &&
        slot?.providerId === currentSlot?.providerId;

      if (isDeselecting) {
        setIntakeTimeSlot(null);
        setPrimaryTag(undefined);
        if (isRecurringSchedulingEnabled) {
          appointmentsPanelDispatch({ type: 'CLEAR_SELECTIONS' });
        }
        return;
      }

      setIntakeTimeSlot(slot);
      if (!slot) {
        setPrimaryTag(undefined);
        return;
      }

      setPrimaryTag(
        getProviderPrimaryTag(recommendationsData, slot.providerId),
      );
      const tier = getProviderTier(
        tieredProviderMapRef.current,
        slot.providerId,
      );
      trackPAEvent(
        ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_INTAKE_SLOT_SELECTED,
        {
          providerId: slot.providerId,
          startTime: slot.startTime,
          tier,
          matchesPatientPreference: slot.isPatientAvailabilityPreferenceMatch,
        },
      );

      if (isRecurringSchedulingEnabled) {
        const provider = providerMapRef.current[slot.providerId];
        const providerName = provider
          ? getDisplayNameOrLegalNameForProvider(
              provider.displayFirstName,
              provider.firstName,
              provider.displayLastName,
              provider.lastName,
            )
          : undefined;
        if (intakeAppointmentType?.appointmentType && providerName) {
          appointmentsPanelDispatch({
            type: 'SET_INTAKE_APPOINTMENT',
            payload: {
              providerType,
              startTime: slot.startTime,
              endTime: slot.endTime,
              appointmentTypeLabel: intakeAppointmentType.appointmentType,
              providerName,
              providerAvatar: provider?.avatar?.url,
            },
          });
        }
      }
    },
    [appointmentsPanelDispatch, intakeAppointmentType?.appointmentType, isRecurringSchedulingEnabled, providerType, recommendationsData, setPrimaryTag, trackPAEvent],
  );

  const onProviderOnlySelect = useCallback(
    (providerId: string) => {
      const isDeselecting = providerId === selectedProviderId;
      setSelectedProviderId(isDeselecting ? null : providerId);
      const tag = isDeselecting
        ? undefined
        : getProviderPrimaryTag(recommendationsData, providerId);
      setPrimaryTag(tag);
    },
    [recommendationsData, selectedProviderId, setPrimaryTag],
  );


  const [updateProviderTeamPreliminaryAssignments] =
    useUpdateProviderTeamPreliminaryAssignmentsMutation();
  const [updateProviderTeamAssignments] =
    useUpdateProviderTeamAssignmentsMutation();
  const [batchSaveAppointments] = useBatchSaveAppointmentsMutation();

  /** Provider ID of the auto-assigned MA, if the IQ API returns one */
  const maProviderId = useMemo(() => {
    const maDisplayName =
      CareTeamProviderTypeNames[CareTeamProviderType.MEDICAL_ASSISTANT];
    const maOption = recommendationsData?.assignmentOptions?.find(
      (o) => o.providerType.toLowerCase() === maDisplayName.toLowerCase(),
    );
    const tier1 = maOption?.tiers?.find((t) => t.tier === 1);
    return tier1?.providers?.[0]?.userId ?? null;
  }, [recommendationsData]);

  const buildProviderAssignments = useCallback(
    (providerId: string) => {
      const provider = providerMap?.[providerId];
      const providerTypeName = getPreferredProviderRoleName({
        fallbackRole: CareTeamProviderTypeNames[providerType],
        provider,
      });
      const assignments: Array<
        Omit<ProviderTeamAssignment, 'assignmentId' | 'startDate'>
      > = [{ providerId, role: providerTypeName, temporary: false }];
      if (maProviderId) {
        const maProvider = providerMap?.[maProviderId];
        const maProviderTypeName = getPreferredProviderRoleName({
          fallbackRole:
            CareTeamProviderTypeNames[CareTeamProviderType.MEDICAL_ASSISTANT],
          provider: maProvider,
        });
        assignments.push({
          providerId: maProviderId,
          role: maProviderTypeName,
          temporary: false,
        });
      }
      return assignments;
    },
    [providerType, maProviderId, providerMap],
  );

  const isProcessingData =
    isPatientLoading ||
    isClinicalProfileLoading ||
    isInquiryLoading ||
    isTreatmentEpisodesLoading ||
    isProviderRecommendationsLoading ||
    isSubmitting;

  const submitProviderAssignmentDecision = useCallback(
    async (providerId: string) => {
      setIsSubmitting(true);
      try {
        const newAssignments = buildProviderAssignments(providerId);
        const providerTeamAssignments = [
          ...committedAssignmentsRef.current,
          ...newAssignments,
        ];
        if (isEnrolled && treatmentEpisodeId) {
          await updateProviderTeamAssignments({
            inquiryId,
            patientId,
            providerTeamAssignments,
            treatmentEpisodeId,
          }).unwrap();
        } else {
          await updateProviderTeamPreliminaryAssignments({
            inquiryId,
            patientId,
            providerTeamAssignments,
          }).unwrap();
        }
      } catch (error) {
        setIsSubmitError(true);
        logError(error, {
          context: 'ProviderAssignmentModal.submitProviderAssignmentDecision',
          errorDataType: typeof error?.data,
          errorDataKeys: error?.data ? Object.keys(error.data) : undefined,
          errorDataSample:
            typeof error?.data === 'string'
              ? error.data.slice(0, 500)
              : JSON.stringify(error?.data || {}).slice(0, 500),
          inquiryId,
          isEnrolled,
          patientId,
          providerId,
          treatmentEpisodeId,
        });

        const fields = parseMissingFieldErrors(error);
        if (fields.length > 0) {
          setSubmitMissingFields(fields);
        } else {
          // Don't create toast - error message is displayed in ActionBar
          setSubmitErrorMessage('Failed to assign provider. Please try again.');
        }
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      buildProviderAssignments,
      inquiryId,
      isEnrolled,
      logError,
      patientId,
      treatmentEpisodeId,
      updateProviderTeamAssignments,
      updateProviderTeamPreliminaryAssignments,
    ],
  );

  const rollbackProviderAssignment = useCallback(
    async (
      providerTeamAssignments: Array<
        Omit<ProviderTeamAssignment, 'assignmentId' | 'startDate'>
      >,
    ) => {
      if (isEnrolled && treatmentEpisodeId) {
        await updateProviderTeamAssignments({
          patientId,
          providerTeamAssignments,
          treatmentEpisodeId,
        }).unwrap();
      } else {
        await updateProviderTeamPreliminaryAssignments({
          patientId,
          inquiryId,
          providerTeamAssignments,
        }).unwrap();
      }
    },
    [
      inquiryId,
      isEnrolled,
      patientId,
      treatmentEpisodeId,
      updateProviderTeamAssignments,
      updateProviderTeamPreliminaryAssignments,
    ],
  );

  /**
   * Validates a slot's UTC times against the patient's timezone and, when a displayed
   * time string is provided, that it matches the recomputed conversion. Guards against
   * booking a stale/mismatched slot - protects intake and every follow-up alike, since
   * all of them go into the same all-or-nothing batch.
   */
  const validateSlotOrThrow = useCallback(
    (slot: { startTime: string; endTime: string; formattedStartEndTime?: string }) => {
      const validationResult = validateTimeSlotInTimezone({
        startTimeUTC: slot.startTime,
        endTimeUTC: slot.endTime,
        displayedStartTime: slot.formattedStartEndTime,
        patientTimezone,
        patientId,
      });

      if (!validationResult.isValid) {
        logError(validationResult.error!, validationResult.errorContext!);
        throw validationResult.error;
      }

      if (validationResult.mismatchDetected && slot.formattedStartEndTime) {
        const expectedStartTime = validationResult
          .startTimeInPatientTZ!.toFormat('h:mm')
          .toLowerCase()
          .replace(':00', '');

        logTimezoneMismatch(logError, {
          patientId,
          patientTimezone,
          displayedStartTime: slot.formattedStartEndTime,
          expectedStartTime,
          startTimeUTC: slot.startTime,
        });

        throw new Error(
          'Displayed appointment time does not match patient timezone conversion',
        );
      }
    },
    [logError, patientId, patientTimezone],
  );

  const {
    isIntakePatientSelected,
    isRecurringPatientSelected,
    intakeSupportAttendeeIds,
    followUpSupportAttendeeIds,
  } = useMemo(
    () => ({
      isIntakePatientSelected: intakeAttendees.some(
        (a) => a.externalId === patientId && a.isSelected,
      ),
      isRecurringPatientSelected: recurringAttendees.some(
        (a) => a.externalId === patientId && a.isSelected,
      ),
      intakeSupportAttendeeIds: intakeAttendees
        .filter((a) => a.userType === 'support' && a.isSelected)
        .map((s) => s.externalId),
      followUpSupportAttendeeIds: recurringAttendees
        .filter((a) => a.userType === 'support' && a.isSelected)
        .map((s) => s.externalId),
    }),
    [intakeAttendees, patientId, recurringAttendees],
  );

  const submitBookingDecision = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const followUps = selectedFollowUpsRef.current;

      if (!intakeAppointmentType?.appointmentTypeLabel) {
        throw new Error(
          'Cannot submit booking: intake appointment type could not be resolved',
        );
      }
      if (followUps.length > 0 && !recurringAppointmentType?.appointmentTypeLabel) {
        throw new Error(
          'Cannot submit booking: recurring appointment type could not be resolved',
        );
      }

      validateSlotOrThrow(intakeTimeSlot);
      followUps.forEach(validateSlotOrThrow);

      const commonProps: BatchSaveCommonProps = {
        hostExternalId: intakeTimeSlot.providerId,
        ignoreAvailabilitySettings: false,
        inquiryExternalId: inquiryId,
        patientExternalId: patientId,
        providerAttendeeExternalIds: [],
        recurrenceFrequency: Recurrence.DOES_NOT_REPEAT,
        timeZoneId: patientTimezone,
      };

      const intakeEntry = {
        ...commonProps,
        appointmentEndDateTime: toUtcIsoString(intakeTimeSlot.endTime),
        appointmentStartDateTime: toUtcIsoString(intakeTimeSlot.startTime),
        appointmentType: intakeAppointmentType.appointmentTypeLabel,
        isPatientAttending: intakeAttendees.some(
          (a) => a.externalId === patientId && a.isSelected,
        ),
        supportAttendeeExternalIds: intakeSupportAttendeeIds,
      };

      const followUpEntries = followUps.map((opening) => ({
        ...commonProps,
        appointmentEndDateTime: toUtcIsoString(opening.endTime),
        appointmentStartDateTime: toUtcIsoString(opening.startTime),
        appointmentType: recurringAppointmentType?.appointmentTypeLabel ?? '',
        isPatientAttending: isRecurringPatientSelected,
        supportAttendeeExternalIds: followUpSupportAttendeeIds,
      }));

      await batchSaveAppointments({
        appointments: [intakeEntry, ...followUpEntries],
      }).unwrap();
    } catch (error) {
      setIsSubmitError(true);

      const errorCode = extractErrorCode(error);
      const errorMessage = getBookingErrorMessage(errorCode);

      setSubmitErrorMessage(errorMessage);

      logError(error, {
        context: 'ProviderAssignmentModal.submitBookingDecision',
        errorCode,
        errorMessage,
        errorDataType: typeof error?.data,
        errorDataKeys: error?.data ? Object.keys(error.data) : undefined,
        errorDataSample:
          typeof error?.data === 'string'
            ? error.data.slice(0, 500)
            : JSON.stringify(error?.data || {}).slice(0, 500),
        intakeAppointmentType: intakeAppointmentType?.appointmentTypeLabel,
        patientId,
        providerId: intakeTimeSlot?.providerId,
        recurringAppointmentType: recurringAppointmentType?.appointmentTypeLabel,
      });

      trackPAEvent(
        ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_SUBMISSION_FAILURE,
        {
          errorCode,
          errorMessage,
          appointmentType: intakeAppointmentType?.appointmentTypeLabel,
          providerId: intakeTimeSlot?.providerId,
        },
      );

      const fields = parseMissingFieldErrors(error);
      if (fields.length > 0) {
        setSubmitMissingFields(fields);
      }
      // Don't create toast - error message is displayed in ActionBar via submitErrorMessage
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    batchSaveAppointments,
    followUpSupportAttendeeIds,
    intakeAppointmentType?.appointmentTypeLabel,
    intakeAttendees,
    intakeSupportAttendeeIds,
    inquiryId,
    intakeTimeSlot,
    isRecurringPatientSelected,
    logError,
    patientId,
    patientTimezone,
    recurringAppointmentType?.appointmentTypeLabel,
    trackPAEvent,
    validateSlotOrThrow,
  ]);

  const onClose = useCallback(() => {
    if (!isCompletedRef.current) {
      trackPAEvent(
        ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_ABANDONED,
      );
    }
    setComponentVisibility({
      component: PortalComponent.PROVIDER_ASSIGNMENT_MODAL,
      data: null,
      isVisible: false,
    });
  }, [setComponentVisibility, trackPAEvent]);

  const handlePostSubmit = useCallback(() => {
    if (!providerTypes?.length) {
      onClose();
      return;
    }

    const currentProviderTypeIndex = providerTypes.indexOf(providerType);

    if (currentProviderTypeIndex === -1) {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: 'provider-type-not-found-toast',
        text: `Unable to get recommendations for ${providerType}. Please try again or contact support.`,
        variant: 'warning',
      });
      logError(new Error('Provider type not found in requested types'), {
        extra: {
          message: 'Provider type missing from requested providerTypes array',
          providerType,
          requestedProviderTypes: providerTypes,
        },
        tags: { logFrom: 'ProviderAssignmentModal.handlePostSubmit' },
      });
      onClose();
      return;
    }

    const isFinalProviderType =
      currentProviderTypeIndex === providerTypes.length - 1;

    if (isFinalProviderType) {
      isCompletedRef.current = true;
      const hasIntakeSlot = !!intakeTimeSlot;
      let tier: ProviderAssignmentSelectionTab | undefined;
      let matchesPatientPreferences = false;

      if (hasIntakeSlot && intakeTimeSlot?.providerId) {
        tier = getProviderTier(
          tieredProviderMapRef.current,
          intakeTimeSlot.providerId,
        );
        matchesPatientPreferences =
          !!intakeTimeSlot.isPatientAvailabilityPreferenceMatch;
      } else if (selectedProviderId) {
        tier = getProviderTier(
          tieredProviderMapRef.current,
          selectedProviderId,
        );
      }

      trackPAEvent(
        ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_COMPLETED,
        { hasIntakeSlot, matchesPatientPreferences, sessionId, tier },
      );
      onClose();
    } else {
      const nextProviderType = providerTypes[currentProviderTypeIndex + 1];
      setProviderType(nextProviderType);
      setIntakeAppointmentType(defaultIntakeAppointmentTypes[nextProviderType]);
      setIntakeTimeSlot(null);
      setSelectedProviderId(null);
      setSelectedTab(ProviderAssignmentSelectionTab.STRONG);
      setRecurringAppointmentType(null);
      setRecurringAttendees([]);
      setStep(Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
    }
  }, [
    providerTypes,
    providerType,
    onClose,
    selectedProviderId,
    sessionId,
    trackPAEvent,
    setProviderType,
    setIntakeAppointmentType,
    createToast,
    logError,
    intakeTimeSlot,
  ]);

  const handleConfirmationSubmit = useCallback(async () => {
    const providerId = intakeTimeSlot?.providerId ?? selectedProviderId;
    const previousAssignments = [...committedAssignmentsRef.current];
    setIsSubmitting(true);

    try {
      await submitProviderAssignmentDecision(providerId);
      committedAssignmentsRef.current = [
        ...previousAssignments,
        ...buildProviderAssignments(providerId),
      ];
    } catch (error) {
      logError(new Error('Error assigning provider', { cause: error }));
      setIsSubmitError(true);
      setIsSubmitting(false);
      return;
    }

    if (intakeTimeSlot) {
      try {
        await submitBookingDecision();
      } catch (error) {
        logError(new Error('Error booking appointment', { cause: error }));
        try {
          await rollbackProviderAssignment(previousAssignments);
        } catch (rollbackError) {
          logError(
            new Error('Error rolling back provider assignment', {
              cause: rollbackError,
            }),
          );
        }
        setIsSubmitError(true);
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(false);
    handlePostSubmit();
  }, [
    buildProviderAssignments,
    handlePostSubmit,
    intakeTimeSlot,
    logError,
    rollbackProviderAssignment,
    selectedProviderId,
    submitBookingDecision,
    submitProviderAssignmentDecision,
  ]);

  const isFollowUpWindowExpiredRef = useRef(false);

  const onPrimaryCtaClick = useCallback(async () => {
    setIsSubmitError(false);
    // Only the CONFIRMATION step's critical-data-missing banner is dismissable via
    // retry; other steps must keep showing a live error until it actually resolves.
    if (step === Step.CONFIRMATION) {
      setIsDismissedCriticalDataError(true);
    }
    setSubmitErrorMessage(undefined);
    setSubmitMissingFields([]);
    switch (step) {
      case Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
        setStep(Step.PROVIDER_AND_INTAKE_SELECTION);
        break;
      case Step.PROVIDER_AND_INTAKE_SELECTION:
        setPreviousStep(Step.PROVIDER_AND_INTAKE_SELECTION);
        if (
          isRecurringSchedulingEnabled &&
          intakeTimeSlot &&
          providerType !== CareTeamProviderType.MEDICAL_PROVIDER
        ) {
          setStep(Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
        } else {
          setStep(Step.CONFIRMATION);
        }
        break;
      case Step.PROVIDER_SELECTION:
        setPreviousStep(Step.PROVIDER_SELECTION);
        setStep(Step.CONFIRMATION);
        break;
      case Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
        setPreviousStep(Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
        if (isFollowUpWindowExpiredRef.current) {
          followUpScheduling.rejectSelection();
          appointmentsPanelDispatch({
            type: 'SET_FOLLOW_UP_APPOINTMENTS',
            payload: { appointments: {} },
          });
          setStep(Step.CONFIRMATION);
        } else {
          setStep(Step.FOLLOW_UP_SCHEDULING);
        }
        break;
      case Step.FOLLOW_UP_SCHEDULING:
        followUpScheduling.markAccepted();
        setPreviousStep(Step.FOLLOW_UP_SCHEDULING);
        setStep(Step.CONFIRMATION);
        break;
      case Step.CONFIRMATION:
        await handleConfirmationSubmit();
        break;
      default:
        break;
    }
    // followUpScheduling is declared later in this component (below, near the
    // FOLLOW_UP_SCHEDULING step wiring); its markAccepted and rejectSelection
    // are stable (deps: []) callbacks, so it's safe to omit here — including
    // them in this array would read `followUpScheduling` before its
    // declaration executes. isFollowUpWindowExpiredRef and
    // appointmentsPanelDispatch are a ref and a useReducer dispatch (stable
    // identities).
  }, [
    handleConfirmationSubmit,
    intakeTimeSlot,
    isRecurringSchedulingEnabled,
    providerType,
    step,
  ]);

  const showNoRecommendationsWarning = useMemo(() => {
    const isRelevantStep = [
      Step.PROVIDER_AND_INTAKE_SELECTION,
      Step.PROVIDER_SELECTION,
    ].includes(step);

    if (!isRelevantStep) {
      return false;
    }

    if (isProviderRecommendationsLoading || isProviderRecommendationsError) {
      return false;
    }

    const hasValidData =
      recommendationsData?.assignmentOptions &&
      Array.isArray(recommendationsData.assignmentOptions);

    if (!hasValidData) {
      return false;
    }

    const providerCount = getTotalProviderCount(
      recommendationsData,
      providerType,
    );

    return providerCount === 0;
  }, [
    recommendationsData,
    isProviderRecommendationsLoading,
    isProviderRecommendationsError,
    step,
    providerType,
  ]);

  const { data: intakeAssessmentData } = useGetAssessmentsListQuery(
    { filterBy: { surveyName: SurveyName.ADMISSIONS_INTAKE }, patientId },
    { skip: !patientId },
  );

  const intakeAssessment = useMemo(() => {
    if (!intakeAssessmentData?.assessments?.length || !inquiryId) return null;
    return intakeAssessmentData.assessments.find(
      (a) => a.inquiryId === inquiryId,
    );
  }, [intakeAssessmentData, inquiryId]);

  const patientAvailabilityPreferences = useMemo(
    () => extractPatientAvailabilityPreferences(intakeAssessment),
    [intakeAssessment],
  );

  const { admitDate, admitDateDisplay } = getAdmitDate(
    inquiryDetail,
    patientClinicalProfile,
  );

  const appointmentsPanelWeekBuckets = useMemo(
    () => buildWeekBuckets(admitDate),
    [admitDate],
  );

  const appointmentsPanelStartISO = useMemo(() => {
    if (!appointmentsPanelWeekBuckets.length) return undefined;
    return appointmentsPanelWeekBuckets[0].start.toISO();
  }, [appointmentsPanelWeekBuckets]);

  const appointmentsPanelEndISO = useMemo(() => {
    if (!appointmentsPanelWeekBuckets.length) return undefined;
    return appointmentsPanelWeekBuckets[appointmentsPanelWeekBuckets.length - 1].end.toISO();
  }, [appointmentsPanelWeekBuckets]);

  const {
    data: appointmentsPanelData,
    isFetching: isAppointmentsPanelLoading,
  } = useGetAppointmentDetailsQuery(
    {
      endDateISO: appointmentsPanelEndISO,
      includeTimezoneDetails: true,
      inquiryIds: [inquiryId],
      pageSize: 100,
      patientId,
      startDateISO: appointmentsPanelStartISO,
      statuses: [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED],
    },
    {
      skip:
        !patientId ||
        !appointmentsPanelStartISO ||
        !appointmentsPanelEndISO,
    },
  );

  const selectedRecurringSupportIds = useMemo(
    () =>
      recurringAttendees
        .filter((a) => a.userType === 'support' && a.isSelected)
        .map((s) => s.externalId),
    [recurringAttendees],
  );

  const {
    availabilityData,
    availableSlots,
    isError: isProviderAvailabilityError,
    isFetching: isProviderAvailabilityLoading,
  } = useProviderAvailability({
    admitDate,
    appointmentType: intakeAppointmentType?.appointmentTypeLabel,
    recommendationsData,
    patientId: isIntakePatientSelected ? patientId : undefined,
    skip:
      step !== Step.PROVIDER_AND_INTAKE_SELECTION ||
      !intakeAppointmentType?.appointmentTypeLabel,
    supportIds: intakeSupportAttendeeIds,
  });

  const treatmentProgramCode = getTreatmentProgramCode(
    getTreatmentProgramDisplay(inquiryDetail, patientClinicalProfile),
  );

  const handleIntakeAppointmentTypeChange = useCallback(
    (newAppointmentType: AppointmentType) =>
      setIntakeAppointmentType(newAppointmentType),
    [],
  );

  const handleIntakeAttendeesChange = useCallback(
    (newAttendees: FamilyAttendeeWithSelection[]) => {
      setIntakeAttendees(newAttendees);
    },
    [],
  );

  const handleRecurringAttendeesChange = useCallback(
    (newAttendees: FamilyAttendeeWithSelection[]) => {
      setRecurringAttendees(newAttendees);
    },
    [],
  );

  const { isFetching: isFetchingIntakeAttendees } = useGetAttendees({
    appointmentType: intakeAppointmentType,
    onFetchAttendees: handleIntakeAttendeesChange,
    patient,
    skip:
      step !== Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION ||
      !!intakeAttendees?.length,
    treatmentProgram: treatmentProgramCode,
  });

  const { isFetching: isFetchingRecurringAttendees } = useGetAttendees({
    appointmentType: recurringAppointmentType,
    onFetchAttendees: handleRecurringAttendeesChange,
    patient,
    skip:
      step !== Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION ||
      !recurringAppointmentType ||
      !!recurringAttendees?.length,
    treatmentProgram: treatmentProgramCode,
  });

  const isMedicalProvider = providerType === CareTeamProviderType.MEDICAL_PROVIDER;

  const followUpAvailability = useFollowUpAvailability({
    hostId: intakeTimeSlot?.providerId,
    appointmentType: recurringAppointmentType?.appointmentTypeLabel,
    timezone: patientTimezone,
    anchorStartISO: intakeTimeSlot?.startTime,
    anchorEndISO: intakeTimeSlot?.endTime,
    admitISO: admitDate ?? undefined,
    patientId,
    skip:
      !isRecurringSchedulingEnabled ||
      isMedicalProvider ||
      step !== Step.FOLLOW_UP_SCHEDULING,
  });

  isFollowUpWindowExpiredRef.current = followUpAvailability.isWindowExpired;

  const getFollowUpHostInfo = useCallback(() => {
    const provider = intakeTimeSlot?.providerId && providerMap?.[intakeTimeSlot.providerId]
      ? providerMap[intakeTimeSlot.providerId]
      : undefined;
    const hostName = provider
      ? getDisplayNameOrLegalNameForProvider(
        provider.displayFirstName,
        provider.firstName,
        provider.displayLastName,
        provider.lastName,
      )
      : '';
    return { hostName, hostAvatar: provider?.avatar?.url };
  }, [intakeTimeSlot?.providerId, providerMap]);

  const dispatchFollowUpSet = useCallback(
    (openings: Iterable<[number, FollowUpOpening]>) => {
      const { hostName, hostAvatar } = getFollowUpHostInfo();
      const title = recurringAppointmentType?.appointmentType ?? '';
      const appointments = Object.fromEntries(
        Array.from(openings).map(([, opening]) => {
          const id = `follow-up-${opening.startTime}-${opening.providerId}`;
          return [id, { id, title, startTime: opening.startTime, endTime: opening.endTime, hostName, hostAvatar }];
        }),
      );
      appointmentsPanelDispatch({ type: 'SET_FOLLOW_UP_APPOINTMENTS', payload: { appointments } });
    },
    [appointmentsPanelDispatch, getFollowUpHostInfo, recurringAppointmentType?.appointmentType],
  );

  const handleFollowUpAutoSelect = useCallback(
    (selected: FollowUpSeries) => {
      dispatchFollowUpSet(selected.openingsByWeek);
    },
    [dispatchFollowUpSet],
  );

  const followUpScheduling = useFollowUpScheduling({
    series: followUpAvailability.series,
    bookableWeekCount: followUpAvailability.bookableWeekCount,
    preferences: patientAvailabilityPreferences,
    timezone: patientTimezone,
    onAutoSelect: handleFollowUpAutoSelect,
  });

  selectedFollowUpsRef.current = followUpScheduling.selectedFollowUps;

  // Event-driven panel sync: dispatch immediately rather than deriving from state
  const handleFollowUpSlotSelect = useCallback(
    (weekIndex: number, opening: FollowUpOpening) => {
      const isDeselecting = followUpScheduling.followUpsByWeek
        .get(weekIndex)
        ?.some(
          (o) =>
            o.startTime === opening.startTime &&
            o.providerId === opening.providerId,
        ) ?? false;

      followUpScheduling.onSlotSelect(weekIndex, opening);

      const id = `follow-up-${opening.startTime}-${opening.providerId}`;
      if (isDeselecting) {
        appointmentsPanelDispatch({ type: 'REMOVE_APPOINTMENT', payload: { id } });
      } else {
        const { hostName, hostAvatar } = getFollowUpHostInfo();
        appointmentsPanelDispatch({
          type: 'ADD_APPOINTMENT',
          payload: {
            id,
            title: recurringAppointmentType?.appointmentType ?? '',
            startTime: opening.startTime,
            endTime: opening.endTime,
            hostName,
            hostAvatar,
          },
        });
      }

      // Open the corresponding week in the appointments panel
      const targetStart = DateTime.fromISO(opening.startTime);
      const idx = appointmentsPanelWeekBuckets.findIndex(
        (bucket) => targetStart >= bucket.start && targetStart <= bucket.end,
      );
      if (idx !== -1) {
        setPanelFocusWeekIndex(idx);
      }
    },
    [
      appointmentsPanelDispatch,
      appointmentsPanelWeekBuckets,
      followUpScheduling.followUpsByWeek,
      followUpScheduling.onSlotSelect,
      getFollowUpHostInfo,
      recurringAppointmentType?.appointmentType,
    ],
  );

  const handleFollowUpSeriesSelect = useCallback(
    (series: FollowUpSeries) => {
      followUpScheduling.onSeriesSelect(series);
      dispatchFollowUpSet(series.openingsByWeek);
    },
    [dispatchFollowUpSet, followUpScheduling.onSeriesSelect],
  );

  const handleFollowUpBestMatchSelect = useCallback(() => {
    followUpScheduling.onBestMatchSelect();
    if (followUpScheduling.bestMatch) {
      dispatchFollowUpSet(followUpScheduling.bestMatch.openingsByWeek);
    }
  }, [dispatchFollowUpSet, followUpScheduling.bestMatch, followUpScheduling.onBestMatchSelect]);

  const handleFollowUpSeriesClear = useCallback(() => {
    followUpScheduling.onSeriesClear();
    appointmentsPanelDispatch({ type: 'SET_FOLLOW_UP_APPOINTMENTS', payload: { appointments: {} } });
  }, [appointmentsPanelDispatch, followUpScheduling.onSeriesClear]);

  // Reset follow-up selections only when entering the follow-up step from a
  // prior step (forward navigation). Do NOT reset when returning from
  // confirmation via the Back button — user selections should be preserved.
  const prevStepRef = useRef(step);
  useEffect(() => {
    const enteringFromPriorStep =
      step === Step.FOLLOW_UP_SCHEDULING &&
      prevStepRef.current !== Step.FOLLOW_UP_SCHEDULING &&
      prevStepRef.current !== Step.CONFIRMATION;
    prevStepRef.current = step;
    if (!enteringFromPriorStep) return;

    followUpScheduling.reinitializeSelection();
    appointmentsPanelDispatch({ type: 'SET_FOLLOW_UP_APPOINTMENTS', payload: { appointments: {} } });
  }, [step, followUpScheduling.reinitializeSelection, appointmentsPanelDispatch]);

  const {
    followUpsByWeek: followUpsByWeekForAnalytics,
    followUpAppointmentCount: followUpCountForAnalytics,
    activeSeriesKey: activeSeriesKeyForAnalytics,
  } = followUpScheduling;

  useEffect(() => {
    if (step === Step.CONFIRMATION && previousStep === Step.FOLLOW_UP_SCHEDULING) {
      const skipped = followUpCountForAnalytics === 0;
      trackPAEvent(
        ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_FOLLOW_UP_SCHEDULING_COMPLETED,
        {
          admit_date: admitDate ?? null,
          chip_key: activeSeriesKeyForAnalytics,
          count_total: followUpCountForAnalytics,
          count_week_1: followUpsByWeekForAnalytics.get(0)?.length ?? 0,
          count_week_2: followUpsByWeekForAnalytics.get(1)?.length ?? 0,
          count_week_3: followUpsByWeekForAnalytics.get(2)?.length ?? 0,
          count_week_4: followUpsByWeekForAnalytics.get(3)?.length ?? 0,
          skipped,
        },
      );
    }
  }, [step, previousStep, admitDate, followUpsByWeekForAnalytics, followUpCountForAnalytics, activeSeriesKeyForAnalytics, trackPAEvent]);

  const title = useMemo(() => {
    return providerType ? `Add ${providerType}` : '';
  }, [providerType]);

  const primaryCta = useMemo(() => {
    const isIntakeAtLeastOneAttendeeSelected = !!intakeAttendees?.some((a) => a.isSelected);
    const isIntakeTimeSlotSelected = !!intakeTimeSlot;
    const isProviderSelected = !!selectedProviderId;

    let label = 'Continue';
    let leftIcon: FAIconName = null;
    if (step === Step.CONFIRMATION) {
      leftIcon = FAIconName.userCheckRegular;
      if (isRecurringSchedulingEnabled) {
        label = 'Confirm selection';
      } else {
        label = `Add ${CareTeamProviderTypeNames[providerType]}`;
      }
    }

    const isRecurringAtLeastOneAttendeeSelected = !!recurringAttendees?.some(
      (a) => a.isSelected,
    );

    return {
      isDisabled:
        isProcessingData ||
        (step === Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION &&
          (!isIntakeAtLeastOneAttendeeSelected ||
            !intakeAppointmentType?.appointmentTypeLabel)) ||
        (step === Step.PROVIDER_AND_INTAKE_SELECTION &&
          !isIntakeTimeSlotSelected) ||
        (step === Step.PROVIDER_SELECTION && !isProviderSelected) ||
        (step === Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION &&
          (isFetchingRecurringAttendees ||
            !isRecurringAtLeastOneAttendeeSelected ||
            !recurringAppointmentType?.appointmentTypeLabel)),
      label,
      leftIcon,
      onClick: onPrimaryCtaClick,
    };
  }, [
    intakeAppointmentType,
    intakeAttendees,
    intakeTimeSlot,
    isFetchingRecurringAttendees,
    isProcessingData,
    isRecurringSchedulingEnabled,
    onPrimaryCtaClick,
    providerType,
    recurringAppointmentType,
    recurringAttendees,
    selectedProviderId,
    step,
  ]);

  const secondaryCta = useMemo(() => {
    const backTo = (targetStep: Step) => ({
      isDisabled: isProcessingData,
      label: 'Back',
      leftIcon: FAIconName.arrowLeftRegular,
      onClick: () => {
        setIsSubmitError(false);
        // Only the CONFIRMATION step's critical-data-missing banner is dismissable via
        // navigation (see submit-retry flow); other steps must keep showing a live error.
        if (step === Step.CONFIRMATION) {
          setIsDismissedCriticalDataError(true);
        }
        setSubmitErrorMessage(undefined);
        setStep(targetStep);
      },
    });

    switch (step) {
      case Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
        return {
          isDisabled: isProcessingData,
          label: 'Cancel',
          leftIcon: FAIconName.timesRegular,
          onClick: onClose,
        };
      case Step.PROVIDER_SELECTION:
      case Step.PROVIDER_AND_INTAKE_SELECTION:
        return {
          isDisabled: isProcessingData,
          label: 'Back',
          leftIcon: FAIconName.arrowLeftRegular,
          onClick: () => {
            trackPAEvent(
              ProviderAssignmentAnalyticsEvents.PROVIDER_ASSIGNMENT_BACK_NAVIGATED,
              { fromStep: STEP_DISPLAY_NAMES[step] },
            );
            setIsSubmitError(false);
            setSubmitErrorMessage(undefined);
            setStep(Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
          },
        };
      case Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
        return backTo(Step.PROVIDER_AND_INTAKE_SELECTION);
      case Step.FOLLOW_UP_SCHEDULING:
        return backTo(Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
      case Step.CONFIRMATION:
        return backTo(previousStep);
      default:
        return null;
    }
  }, [isProcessingData, onClose, previousStep, step, trackPAEvent]);

  const tertiaryCta = useMemo(() => {
    if (showNoRecommendationsWarning) {
      return null;
    }

    if (
      [
        Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION,
        Step.PROVIDER_AND_INTAKE_SELECTION,
      ].includes(step)
    ) {
      return {
        isDisabled: isProcessingData || !!intakeTimeSlot,
        label: 'Assign without scheduling',
        onClick: () => {
          followUpScheduling.rejectSelection();
          appointmentsPanelDispatch({
            type: 'SET_FOLLOW_UP_APPOINTMENTS',
            payload: { appointments: {} },
          });
          setStep(Step.PROVIDER_SELECTION);
        },
      };
    }

    if (
      isRecurringSchedulingEnabled &&
      step === Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION
    ) {
      return {
        isDisabled: isProcessingData,
        label: 'Skip for now',
        onClick: () => {
          followUpScheduling.rejectSelection();
          appointmentsPanelDispatch({
            type: 'SET_FOLLOW_UP_APPOINTMENTS',
            payload: { appointments: {} },
          });
          setPreviousStep(Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION);
          setStep(Step.CONFIRMATION);
        },
      };
    }

    if (
      isRecurringSchedulingEnabled &&
      step === Step.FOLLOW_UP_SCHEDULING
    ) {
      return {
        isDisabled: isProcessingData,
        label: 'Skip',
        onClick: () => {
          followUpScheduling.rejectSelection();
          appointmentsPanelDispatch({
            type: 'SET_FOLLOW_UP_APPOINTMENTS',
            payload: { appointments: {} },
          });
          setPreviousStep(Step.FOLLOW_UP_SCHEDULING);
          setStep(Step.CONFIRMATION);
        },
      };
    }

    return null;
  }, [
    appointmentsPanelDispatch,
    followUpScheduling.rejectSelection,
    intakeTimeSlot,
    isProcessingData,
    isRecurringSchedulingEnabled,
    showNoRecommendationsWarning,
    step,
  ]);

  const tieredProviderMap = useMemo<
    Record<
      ProviderAssignmentSelectionTab,
      Record<string, GetProviderByIdResponse>
    >
  >(
    () => getTieredProviderMap(recommendationsData, providerMap, providerType),
    [recommendationsData, providerType, providerMap],
  );

  const tieredProviderMapFiltered = useMemo(
    () =>
      step === Step.PROVIDER_AND_INTAKE_SELECTION
        ? filterProvidersWithAvailability(tieredProviderMap, availableSlots)
        : tieredProviderMap,
    [step, tieredProviderMap, availableSlots],
  );

  const totalProviderCount = useMemo(
    () => getTotalProviderCount(recommendationsData, providerType),
    [recommendationsData, providerType],
  );

  const totalProvidersWithSlots = useMemo(() => {
    if (step !== Step.PROVIDER_AND_INTAKE_SELECTION) return 0;
    return Object.values(tieredProviderMapFiltered).reduce(
      (sum, tierMap) => sum + Object.keys(tierMap).length,
      0,
    );
  }, [step, tieredProviderMapFiltered]);

  const showAvailabilityTooltip = useMemo(
    () =>
      step === Step.PROVIDER_AND_INTAKE_SELECTION &&
      totalProviderCount > MAX_PROVIDERS_FOR_AVAILABILITY_FETCH &&
      totalProvidersWithSlots > 0,
    [step, totalProviderCount, totalProvidersWithSlots],
  );

  const showNoAvailabilityWarning = useMemo(
    () =>
      step === Step.PROVIDER_AND_INTAKE_SELECTION &&
      !isProviderAvailabilityLoading &&
      availabilityData !== undefined &&
      totalProviderCount >= 1 &&
      totalProvidersWithSlots === 0,
    [availabilityData, isProviderAvailabilityLoading, step, totalProviderCount, totalProvidersWithSlots],
  );

  const showFollowUpWindowExpiredWarning =
    step === Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION &&
    followUpAvailability.isWindowExpired;

  const topNProviderIds = useMemo(
    () => new Set(extractProviderIds(recommendationsData)),
    [recommendationsData],
  );

  useEffect(() => {
    tieredProviderMapRef.current = tieredProviderMap;
  }, [tieredProviderMap]);

  useEffect(() => {
    providerMapRef.current = providerMap ?? {};
  }, [providerMap]);

  useEffect(() => {
    intakeTimeSlotRef.current = intakeTimeSlot;
  }, [intakeTimeSlot]);

  let mainContent: ReactNode;
  switch (step) {
    case Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
      mainContent = (
        <AttendeeAndAppointmentTypeSelection
          appointmentType={intakeAppointmentType}
          attendees={intakeAttendees}
          isFetchingAttendees={isFetchingIntakeAttendees}
          key={providerType}
          onAppointmentTypeChange={handleIntakeAppointmentTypeChange}
          onAttendeesChange={handleIntakeAttendeesChange}
          patient={patient}
          providerType={providerType}
        />
      );
      break;
    case Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
      mainContent = (
        <RecurringTypeAndAttendees
          attendees={recurringAttendees}
          onAppointmentTypeChange={setRecurringAppointmentType}
          onAttendeesChange={setRecurringAttendees}
          patient={patient}
          providerType={providerType}
          selectedAppointmentType={recurringAppointmentType}
        />
      );
      break;
    case Step.PROVIDER_AND_INTAKE_SELECTION:
      mainContent = (
        <TimeSlotSelection
          data-testid={`${TOP_LEVEL_ID}__time-slot-selection`}
          defaultTab={selectedTab}
          isLoading={
            isProviderRecommendationsLoading || isProviderAvailabilityLoading
          }
          key={providerType}
          onSelect={onSlotSelect}
          onTabChange={setSelectedTab}
          patientAvailabilityPreferences={patientAvailabilityPreferences}
          patientTimezone={patientTimezone}
          scrollContainerRef={scrollableContainerRef}
          selectedSlot={intakeTimeSlot}
          slots={availableSlots}
          tieredProviderMap={tieredProviderMapFiltered}
          availabilityTooltipMessage={
            !showAvailabilityTooltip
              ? undefined
              : AVAILABILITY_TOOLTIP_MESSAGE
          }
        />
      );
      break;
    case Step.PROVIDER_SELECTION:
      mainContent = (
        <ProviderOnlySelection
          defaultTab={selectedTab}
          isLoading={isProviderRecommendationsLoading}
          key={providerType}
          onSelect={onProviderOnlySelect}
          onTabChange={setSelectedTab}
          selectedProviderId={selectedProviderId}
          tieredProviderMap={tieredProviderMap}
          availableSlots={availableSlots}
          topNProviderIds={topNProviderIds}
        />
      );
      break;
    case Step.FOLLOW_UP_SCHEDULING:
      mainContent = (
        <FollowUpScheduling
          activeSeriesKey={followUpScheduling.activeSeriesKey}
          buckets={followUpAvailability.buckets}
          bufferMinutes={recurringAppointmentType?.bufferInMinutes}
          hostId={followUpAvailability.hostId}
          appointmentType={followUpAvailability.appointmentType}
          isLoading={followUpAvailability.isFetching}
          onBestMatchSelect={handleFollowUpBestMatchSelect}
          onSeriesClear={handleFollowUpSeriesClear}
          onSeriesSelect={handleFollowUpSeriesSelect}
          onSlotSelect={handleFollowUpSlotSelect}
          onWindowAvailability={followUpAvailability.reportAvailability}
          onWindowError={followUpAvailability.reportError}
          patientId={isRecurringPatientSelected ? patientId : undefined}
          selectedByWeek={followUpScheduling.followUpsByWeek}
          series={followUpAvailability.series}
          supportIds={selectedRecurringSupportIds}
          windows={followUpAvailability.windows}
        />
      );
      break;
    case Step.CONFIRMATION: {
      const proposedProviderId =
        intakeTimeSlot?.providerId ?? selectedProviderId;
      const proposedProvider = providerMap[proposedProviderId];
      const providerRoleName = CareTeamProviderTypeNames[providerType];

      // No provider selected (e.g., user skipped scheduling) -> show nothing
      if (!proposedProvider) {
        mainContent = null;
      }
      // FF ON -> Use new ConfirmationStep component
      else if (isRecurringSchedulingEnabled) {
        const confirmationAppointments: ConfirmationAppointment[] = [
          ...(intakeTimeSlot
            ? [
              {
                startTime: intakeTimeSlot.startTime,
                endTime: intakeTimeSlot.endTime,
                appointmentType: intakeAppointmentType?.appointmentType ?? '',
                providerId: intakeTimeSlot.providerId,
                isIntake: true,
              },
            ]
            : []),
          ...followUpScheduling.selectedFollowUps.map((opening) => ({
            startTime: opening.startTime,
            endTime: opening.endTime,
            appointmentType: recurringAppointmentType?.appointmentType ?? '',
            providerId: opening.providerId,
            isIntake: false,
          })),
        ];

        mainContent = (
          <ConfirmationStep
            appointments={confirmationAppointments}
            key={providerType}
            patientTimezone={patientTimezone}
            provider={{ ...proposedProvider, role: providerRoleName }}
          />
        );
      }
      // FF OFF -> Use legacy ConfirmationStep component
      else {
        mainContent = (
          <ConfirmationStepLegacy
            appointments={appointmentsPanelData?.data}
            appointmentType={intakeAppointmentType?.appointmentType ?? ''}
            isAppointmentsLoading={isAppointmentsPanelLoading}
            key={providerType}
            patientId={patientId}
            patientTimezone={patientTimezone}
            proposedAppointmentSlot={intakeTimeSlot}
            proposedFollowUps={followUpScheduling.selectedFollowUps}
            proposedProvider={proposedProvider}
            proposedProviderRole={providerRoleName}
            recurringAppointmentType={
              recurringAppointmentType?.appointmentType ?? null
            }
          />
        );
      }
      break;
    }
    default:
      mainContent = null;
  }

  const sidePanel = useMemo(() => {
    const treatmentProgramDisplay = getTreatmentProgramDisplay(
      inquiryDetail,
      patientClinicalProfile,
    );

    switch (step) {
      case Step.PROVIDER_AND_INTAKE_SELECTION:
      case Step.PROVIDER_SELECTION:
      case Step.INTAKE_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
      case Step.RECURRING_ATTENDEES_AND_APPOINTMENT_TYPE_SELECTION:
      case Step.FOLLOW_UP_SCHEDULING:
        return (
          <ChakraVStack spacing="18px" height="100%" minHeight={0} alignItems="stretch">
            <PatientInfoCard
              admitDate={admitDateDisplay}
              diagnosis={patientClinicalProfile?.primaryDiagnosis}
              isLoading={
                isPatientLoading || isClinicalProfileLoading || isInquiryLoading
              }
              patient={patient}
              treatmentProgram={treatmentProgramDisplay}
            />
            {isRecurringSchedulingEnabled ? (
              <AppointmentsPanel
                appointments={appointmentsPanelData?.data || []}
                focusWeekIndex={panelFocusWeekIndex}
                isLoading={isAppointmentsPanelLoading}
                patientTimezone={patientTimezone}
                selectionState={appointmentsPanelState}
                weekBuckets={appointmentsPanelWeekBuckets}
              />
            ) : (
              <PatientAppointments
                admitDate={admitDate}
                inquiryId={inquiryId}
                patientId={patientId}
              />
            )}
          </ChakraVStack>
        );
      default:
        return null;
    }
  }, [
    admitDate,
    admitDateDisplay,
    appointmentsPanelData,
    appointmentsPanelState,
    appointmentsPanelWeekBuckets,
    inquiryDetail,
    inquiryId,
    isAppointmentsPanelLoading,
    isClinicalProfileLoading,
    isInquiryLoading,
    isPatientLoading,
    isRecurringSchedulingEnabled,
    panelFocusWeekIndex,
    patient,
    patientClinicalProfile,
    patientId,
    patientTimezone,
    step,
  ]);

  const isCriticalDataMissing =
    isPatientError ||
    isClinicalProfileError ||
    isInquiryError ||
    isTreatmentEpisodesError ||
    isTreatmentEpisodeMissing ||
    (isProviderRecommendationsError && !iqErrorMessage) ||
    (isProviderRecommendationsError && (missingFields?.length ?? 0) > 0) ||
    isProviderAvailabilityError;

  useEffect(() => {
    if (isCriticalDataMissing) setIsDismissedCriticalDataError(false);
  }, [isCriticalDataMissing]);

  const { openSupportChat } = useSupportChat();

  /**
   * Show an action bar with a relevant message if
   *  - there is an error loading data critical to the workflow
   *  - there is a specific IQ API error message to display
   *  - there is no data returned for provider recommendations or availability
   *  - there is an error submitting the provider assignment or booking decisions
   */
  const actionBar = useMemo(() => {
    const message = getActionBarMessage({
      isCriticalDataMissing: isCriticalDataMissing && !isDismissedCriticalDataError,
      isProviderRecommendationsError,
      iqErrorMessage,
      isSubmitError,
      submitMissingFieldsCount: submitMissingFields.length,
      showNoRecommendationsWarning,
      showNoAvailabilityWarning,
      isFollowUpWindowExpired: showFollowUpWindowExpiredWarning,
    });

    if (!message) return null;

    const fields = submitMissingFields.length > 0 ? submitMissingFields : missingFields;
    let config: { text: ReactNode; variant: ActionBarVariant };

    switch (message) {
      case ActionBarMessage.CRITICAL_DATA_MISSING:
        config = getCriticalDataMissingActionBarConfig(fields);
        break;
      case ActionBarMessage.SUBMIT_ERROR:
        config = getSubmitErrorActionBarConfig(submitErrorMessage);
        break;
      case ActionBarMessage.IQ_ERROR:
        config = getIqErrorActionBarConfig(iqErrorMessage);
        break;
      case ActionBarMessage.NO_RECOMMENDATIONS:
        config = getNoRecommendationsActionBarConfig(providerType);
        break;
      case ActionBarMessage.NO_AVAILABILITY:
        config = getNoAvailabilityActionBarConfig(providerType);
        break;
      case ActionBarMessage.FOLLOW_UP_WINDOW_EXPIRED:
        config = getFollowUpWindowExpiredActionBarConfig();
        break;
    }

    return (
      <ChakraBox width="100%">
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          buttonIcon={isSubmitError ? undefined : FAIconName.headsetRegular}
          buttonText={isSubmitError ? undefined : 'Contact support'}
          id={`${TOP_LEVEL_ID}__data-loading-error`}
          onButtonClick={isSubmitError ? undefined : openSupportChat}
          text={config.text}
          variant={config.variant}
        />
      </ChakraBox>
    );
  }, [
    isCriticalDataMissing,
    isDismissedCriticalDataError,
    isProviderRecommendationsError,
    iqErrorMessage,
    missingFields,
    submitMissingFields,
    submitErrorMessage,
    isSubmitError,
    openSupportChat,
    providerType,
    showFollowUpWindowExpiredWarning,
    showNoAvailabilityWarning,
    showNoRecommendationsWarning,
  ]);

  const isNewConfirmationLayout =
    step === Step.CONFIRMATION && isRecurringSchedulingEnabled;

  return (
    <ModalV2
      footerContent={
        <Footer
          id={`${TOP_LEVEL_ID}__footer`}
          primaryCta={primaryCta}
          primaryCtaAddon={
            step === Step.FOLLOW_UP_SCHEDULING &&
              followUpScheduling.followUpAppointmentCount > 0 ? (
              <Text
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__follow-up-count`}
                variant="body"
              >
                {`${followUpScheduling.followUpAppointmentCount} session${followUpScheduling.followUpAppointmentCount !== 1 ? 's' : ''} selected`}
              </Text>
            ) : undefined
          }
          secondaryCta={secondaryCta}
          tertiaryCta={tertiaryCta}
        >
          {actionBar}
        </Footer>
      }
      hideCloseButton={isSubmitting}
      id={TOP_LEVEL_ID}
      isOpen
      maxWidth={isNewConfirmationLayout ? '500px' : undefined}
      onClose={onClose}
      shouldCloseOnEscape={!isSubmitting}
      shouldCloseOnOverlayClick={!isSubmitting}
      size="6xl"
      title={title}
    >
      <ChakraFlex
        gap="36px"
        height={isNewConfirmationLayout ? 'auto' : '55vh'}
      >
        <ChakraBox
          ref={scrollableContainerRef}
          flex="1"
          height={isNewConfirmationLayout ? 'auto' : '100%'}
          onWheel={(e) => e.stopPropagation()}
          overflowY={isNewConfirmationLayout ? 'visible' : 'auto'}
          tabIndex={0}
        >
          {mainContent}
        </ChakraBox>
        {sidePanel && (
          <ChakraBox
            width="300px"
            height="100%"
            minHeight={0}
            onWheel={(e) => e.stopPropagation()}
          >
            {sidePanel}
          </ChakraBox>
        )}
      </ChakraFlex>
    </ModalV2>
  );
};

export default ProviderAssignmentModal;
